import { useEffect } from 'react';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PrivacyPolicyModal({ isOpen, onClose }: PrivacyPolicyModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="개인정보처리방침"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-stone-100">
          <div>
            <h2 className="text-lg font-bold text-stone-800">개인정보처리방침</h2>
            <p className="text-xs text-stone-400 mt-0.5">최종 업데이트: 2024년 1월 1일</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer text-stone-400 hover:text-stone-700"
            aria-label="닫기"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-8 py-6 text-sm text-stone-600 leading-relaxed space-y-6">

          <div>
            <p className="text-stone-500">
              (주)소담이앤씨(이하 &ldquo;회사&rdquo;)는 정보주체의 자유와 권리 보호를 위해 「개인정보 보호법」 및 관계 법령이 정한 바를 준수하여,
              적법하게 개인정보를 처리하고 안전하게 관리하고 있습니다.
            </p>
          </div>

          <section>
            <h3 className="font-bold text-stone-800 mb-2">제1조 (개인정보의 처리 목적)</h3>
            <p>
              회사는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며,
              이용 목적이 변경되는 경우에는 「개인정보 보호법」 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.
            </p>
            <ul className="mt-2 pl-4 space-y-1 list-disc text-stone-500">
              <li>무료 상담 신청 및 고객 문의 처리</li>
              <li>견적 요청 및 서비스 계약 체결</li>
              <li>서비스 제공 및 계약 이행</li>
              <li>민원 처리 및 고충 해결</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold text-stone-800 mb-2">제2조 (처리하는 개인정보의 항목)</h3>
            <p>회사는 다음의 개인정보 항목을 처리하고 있습니다.</p>
            <ul className="mt-2 pl-4 space-y-1 list-disc text-stone-500">
              <li><strong className="text-stone-700">필수항목:</strong> 성명, 연락처(전화번호), 이메일 주소</li>
              <li><strong className="text-stone-700">선택항목:</strong> 소속 기관명, 현장 주소, 상담 내용</li>
              <li><strong className="text-stone-700">자동수집항목:</strong> IP 주소, 접속 로그, 쿠키, 접속 기기 정보</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold text-stone-800 mb-2">제3조 (개인정보의 처리 및 보유 기간)</h3>
            <p>
              회사는 법령에 따른 개인정보 보유·이용 기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보
              보유·이용 기간 내에서 개인정보를 처리·보유합니다.
            </p>
            <ul className="mt-2 pl-4 space-y-1 list-disc text-stone-500">
              <li>상담 및 문의 기록: 상담 종료 후 3년</li>
              <li>계약 관련 정보: 계약 만료 후 5년</li>
              <li>전자상거래 관련 기록: 관련 법령에 따라 5년 보관</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold text-stone-800 mb-2">제4조 (개인정보의 제3자 제공)</h3>
            <p>
              회사는 정보주체의 개인정보를 제1조(개인정보의 처리 목적)에서 명시한 범위 내에서만 처리하며,
              정보주체의 동의, 법률의 특별한 규정 등 「개인정보 보호법」 제17조 및 제18조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.
            </p>
          </section>

          <section>
            <h3 className="font-bold text-stone-800 mb-2">제5조 (개인정보의 파기)</h3>
            <p>
              회사는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는
              지체 없이 해당 개인정보를 파기합니다.
            </p>
            <ul className="mt-2 pl-4 space-y-1 list-disc text-stone-500">
              <li><strong className="text-stone-700">전자적 파일:</strong> 복원이 불가능한 방법으로 영구 삭제</li>
              <li><strong className="text-stone-700">종이 문서:</strong> 분쇄 또는 소각하여 파기</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold text-stone-800 mb-2">제6조 (정보주체의 권리·의무 및 행사 방법)</h3>
            <p>정보주체는 회사에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다.</p>
            <ul className="mt-2 pl-4 space-y-1 list-disc text-stone-500">
              <li>개인정보 열람 요구</li>
              <li>오류 등이 있을 경우 정정 요구</li>
              <li>삭제 요구</li>
              <li>처리 정지 요구</li>
            </ul>
          </section>

          <section>
            <h3 className="font-bold text-stone-800 mb-2">제7조 (개인정보 보호책임자)</h3>
            <div className="bg-stone-50 rounded-xl p-4 mt-2 space-y-1 text-stone-500">
              <div><strong className="text-stone-700">회사명:</strong> (주)소담이앤씨</div>
              <div><strong className="text-stone-700">개인정보 보호책임자</strong></div>
              <div>주소: 경기도 화성시 만세구 꽃내음1길 5-4, 510호</div>
              <div>전화: <a href="tel:031-8043-8611" className="text-green-700 hover:underline cursor-pointer">031-8043-8611</a></div>
              <div>이메일: <a href="mailto:sodamenc0728@daum.net" className="text-green-700 hover:underline cursor-pointer">sodamenc0728@daum.net</a></div>
            </div>
          </section>

          <section>
            <h3 className="font-bold text-stone-800 mb-2">제8조 (권익침해 구제 방법)</h3>
            <p>
              정보주체는 아래의 기관에 대해 개인정보 침해에 대한 피해구제, 상담 등을 문의하실 수 있습니다.
            </p>
            <ul className="mt-2 pl-4 space-y-1 list-disc text-stone-500">
              <li>개인정보보호위원회: (국번없이) 182</li>
              <li>개인정보 침해신고센터: privacy.kisa.or.kr / (국번없이) 118</li>
              <li>대검찰청 사이버수사과: www.spo.go.kr / (국번없이) 1301</li>
              <li>경찰청 사이버안전국: cyberbureau.police.go.kr / (국번없이) 182</li>
            </ul>
          </section>

          <p className="text-stone-400 text-xs pt-2 border-t border-stone-100">
            본 개인정보처리방침은 2024년 1월 1일부터 적용됩니다.
          </p>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-stone-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-green-800 hover:bg-green-900 text-white text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
}
